// İSG uzmanı portalı — İş İzni detayı (api-contract §14/§17). Carbon referans sayfa (S33 planı
// `docs/plans/S33-carbon-reskin.md` §6 Slice 3): Breadcrumb → PageHeader (başlık + Tag'ler +
// ButtonSet) → `DetailLayout` (owner kararı, plan §layout — <1440px Bilgiler+İlerleme yan yana
// üstte/Tabs+Tile tam genişlik altta, ≥1440px 2a'nın `grid-cols-[336px_minmax(0,1fr)]`'i; bkz.
// `@isgportal/shared` `DetailLayout`). Sekmeli gövde: **Kapsanan Personel** (gerçek `Table` +
// satır ⋮ menüsü + `Sheet`, bkz. `PermitPersonnelPanel.tsx`) · **Gerekli Eğitimler** · **KKD** ·
// **Kontrol Listesi** · **Ek Belge Talepleri** · **Araçlar** (`hasVehicles` veya bağlı araç) ·
// **Aksiyonlar**. Tek `GET /permits/{uuid}` izin + gerekli eğitim + personel + readiness'i
// birlikte döndürür.

import {
  Alert,
  Breadcrumb,
  Button,
  ButtonSet,
  Card,
  CardContent,
  DetailLayout,
  HazardTag,
  PageHeader,
  ProgressIndicator,
  Spinner,
  type TabItem,
  TabPanel,
  Tabs,
  Tag,
  type WorkPermitDto,
  countNotReadyPersonnel,
  countNotReadyVehicles,
  currentCulture,
  hasVehicleSection,
  permitsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Pencil } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { PermitActivityPanel } from '../../components/permits/PermitActivityPanel';
import { PermitControlChecklistPanel } from '../../components/permits/PermitControlChecklistPanel';
import { PermitDocumentRequestsPanel } from '../../components/permits/PermitDocumentRequestsPanel';
import { PermitFormModal } from '../../components/permits/PermitFormModal';
import { PermitLifecycleActions } from '../../components/permits/PermitLifecycleActions';
import { PermitPersonnelPanel } from '../../components/permits/PermitPersonnelPanel';
import { PermitProtectiveEquipmentPanel } from '../../components/permits/PermitProtectiveEquipmentPanel';
import { PermitVehiclesPanel } from '../../components/permits/PermitVehiclesPanel';
import { RequiredTrainingsPanel } from '../../components/permits/RequiredTrainingsPanel';
import {
  buildPermitProgressSteps,
  getPermitStatusPresentation,
  isPermitEditable,
  permitIssuesPastApproval,
} from '../../components/permits/permitEnums';
import { formatDate, formatDateTime } from '../../lib/format';
import { permitsKeys } from '../../lib/permitsKeys';

type DetailTab =
  | 'personnel'
  | 'trainings'
  | 'ppe'
  | 'controls'
  | 'docRequests'
  | 'vehicles'
  | 'activities';

/** Etiket + değer satırı (bilgi bloğu). Boş değer tire ile gösterilir. */
function Fact({
  label,
  value,
  className,
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-sm text-fg">{value && value.length > 0 ? value : '—'}</dd>
    </div>
  );
}

/**
 * Sahalar bilgi bloğu (**çoklu lokasyon — §14 Faz 2**): iznin kapsadığı tüm sahaları rozet
 * listesi olarak gösterir; saha yoksa tire. Ada göre sıralı gelir (backend).
 */
function SitesFact({
  label,
  sites,
  className,
}: {
  label: string;
  sites: WorkPermitDto['sites'];
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 flex flex-wrap gap-1.5 text-sm text-fg">
        {sites.length === 0
          ? '—'
          : sites.map((s) => (
              <Tag key={s.uuid} color="gray">
                {s.name}
              </Tag>
            ))}
      </dd>
    </div>
  );
}

/** İsim/plaka listesini kısa tutar (uyarı satırı taşmasın) — 4'ten fazlaysa "+N daha". */
function joinCapped(
  items: string[],
  t: (key: string, options?: Record<string, unknown>) => string,
  limit = 4,
): string {
  if (items.length <= limit) {
    return items.join(', ');
  }
  const shown = items.slice(0, limit).join(', ');
  return `${shown} ${t('permits.detail.issues.moreCount', { count: items.length - limit })}`;
}

export function PermitDetailPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const queryClient = useQueryClient();
  const { permitUuid = '' } = useParams();
  // Geldiği liste — YALNIZ ucuzsa (S33 Slice 4 QA fix, OWNER DECISION §5): `PermitsListPage`
  // satırın `Link`'ine `state`le birlikte kendi geçerli `?status=` filtresinin okunur adını +
  // hedefini iliştirir (ek istek YOK, saf `history.state`). Doğrudan bu sayfaya gelinirse
  // (yer imi, yeni sekme, F5) `state` `null` olur — o zaman jenerik "Çalışma İzinleri" kalır.
  const location = useLocation();
  const fromList = location.state as { backHref?: string; backLabel?: string } | null;

  const [editOpen, setEditOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<DetailTab>('personnel');

  const query = useQuery({
    queryKey: permitsKeys.detail(permitUuid),
    queryFn: () => permitsApi.getPermit(permitUuid),
    enabled: permitUuid.length > 0,
  });

  const detail = query.data;
  const permit = detail?.permit;
  // Düzenlenebilirlik: backend reviewer sinyali (§30.4 `canEdit`) VARSA ona bağlanır (reviewer
  // sonraki açık aşamalarda da düzenler; backend otoritedir); yoksa eski status-bazlı gate'e
  // düşülür (`Draft`/`Submitted` — regresyon yok; brief 098 İş 3).
  const editable = permit ? (detail?.canEdit ?? isPermitEditable(permit.status)) : false;

  const tabItems: TabItem[] = useMemo(() => {
    if (!permit) {
      return [];
    }
    // Rozet sayıları uyarı şeridiyle (aşağıdaki `issueCount`) AYNI `detail` verisinden; araç
    // sayısı yalnız Araçlar sekmesi varsa (`hasVehicles` veya bağlı araç).
    const personnelIncomplete = detail ? countNotReadyPersonnel(detail.personnel) : 0;
    const showVehicles = hasVehicleSection(permit.hasVehicles, detail?.vehicles ?? []);
    const vehiclesIncomplete = detail && showVehicles ? countNotReadyVehicles(detail.vehicles) : 0;
    const items: TabItem[] = [
      {
        value: 'personnel',
        label: t('permits.detail.section.personnel'),
        badge: {
          count: personnelIncomplete,
          srLabel: t('permits.detail.tab.personnelIncomplete', { count: personnelIncomplete }),
        },
      },
      { value: 'trainings', label: t('permits.detail.section.trainings') },
      // KKD + Kontrol sekmeleri tipten gelir (§24.3/§25.3); her zaman gösterilir (boşsa boş
      // durum). KKD read-only; Kontrol checklist İSG uzmanı işaretlemesidir.
      { value: 'ppe', label: t('permits.detail.section.ppe') },
      { value: 'controls', label: t('permits.detail.section.controls') },
      // Ek Belge Talepleri (§30.10): izne özel, tip-standart belgelere EK talep edilen belge
      // türleri. Permit-seviyesi + readiness'e katılır. Her zaman görünür (boşsa boş durum).
      { value: 'docRequests', label: t('permits.detail.section.documentRequests') },
    ];
    // Araçlar sekmesi izinde araç bayrağı VEYA bağlı araç varsa (§14/§23) gösterilir.
    if (showVehicles) {
      items.push({
        value: 'vehicles',
        label: t('permits.detail.section.vehicles'),
        badge: {
          count: vehiclesIncomplete,
          srLabel: t('permits.detail.tab.vehiclesIncomplete', { count: vehiclesIncomplete }),
        },
      });
    }
    // Aksiyonlar sekmesi (SPEC §5): sistem-log + yorum zaman-çizelgesi. Her zaman görünür.
    items.push({ value: 'activities', label: t('permits.detail.section.activities') });
    return items;
  }, [permit, detail, t]);

  // Seçili sekme kaybolabilir (ör. son araç izinden çıkarılınca bayraksız izinde Araçlar sekmesi
  // gider): görünür ilk sekmeye düş ve odak <body>'ye kaçtıysa düşülen sekmenin tetikleyicisine al.
  const activeTab: DetailTab = tabItems.some((i) => i.value === selectedTab)
    ? selectedTab
    : 'personnel';
  useEffect(() => {
    if (tabItems.length === 0 || selectedTab === activeTab) {
      return;
    }
    setSelectedTab(activeTab);
    const active = document.activeElement;
    if (!active || active === document.body) {
      document.getElementById(`permit-detail-tab-${activeTab}`)?.focus();
    }
  }, [tabItems, selectedTab, activeTab]);

  // Uyarı satırı (InlineNotification) — yalnız ZATEN YÜKLENMİŞ veriden istemci tarafında
  // hesaplanır (yeni bir uç YOK): hazır olmayan personel + (varsa) hazır olmayan araç, İSİM/
  // PLAKA dahil (coordinator bulgusu — README "Title + body on next line", "which people/
  // checklist items block"). Kontrol listesi maddeleri kasıtlı DIŞARIDA — o kapı yalnız
  // `Active`'te anlamlı (§25.3) ve burada "engelliyor" anlamına gelmiyor.
  const { issueCount, issuePersonnelNames, issueVehiclePlates } = useMemo(() => {
    if (!detail || !permit) {
      return {
        issueCount: 0,
        issuePersonnelNames: [] as string[],
        issueVehiclePlates: [] as string[],
      };
    }
    const names = detail.personnel
      .filter((p) => !p.ready)
      .map((p) => `${p.firstName} ${p.lastName}`.trim());
    const plates = hasVehicleSection(permit.hasVehicles, detail.vehicles)
      ? detail.vehicles.filter((v) => !v.vehicleReady).map((v) => v.plate)
      : [];
    return {
      issueCount: names.length + plates.length,
      issuePersonnelNames: names,
      issueVehiclePlates: plates,
    };
  }, [detail, permit]);

  const issueFactsLine = useMemo(() => {
    const parts: string[] = [];
    if (issuePersonnelNames.length > 0) {
      parts.push(
        t('permits.detail.issues.personnelFacts', { list: joinCapped(issuePersonnelNames, t) }),
      );
    }
    if (issueVehiclePlates.length > 0) {
      parts.push(
        t('permits.detail.issues.vehicleFacts', { list: joinCapped(issueVehiclePlates, t) }),
      );
    }
    return parts.join(' · ');
  }, [issuePersonnelNames, issueVehiclePlates, t]);

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[
          {
            label: fromList?.backLabel ?? t('permits.title'),
            href: fromList?.backHref ?? '/permits',
          },
          ...(permit ? [{ label: permit.title }] : []),
        ]}
      />

      {query.isPending ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Spinner label={t('permits.detail.loading')} />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <ClipboardCheck className="h-10 w-10 text-danger" aria-hidden="true" />
            <p className="text-sm text-muted">{resolveApiErrorMessage(query.error, t)}</p>
            <Button asChild variant="secondary">
              <Link to="/permits">{t('permits.detail.back')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : detail && permit ? (
        <>
          <PageHeader
            title={permit.title}
            tags={
              <>
                {permit.permitTypeName ? <Tag color="blue">{permit.permitTypeName}</Tag> : null}
                {/* Sıra: durum önce, hazard sonra (S34-2 design review §1 "detail header"). */}
                <Tag color={getPermitStatusPresentation(permit.status).tagColor}>
                  {t(getPermitStatusPresentation(permit.status).labelKey)}
                </Tag>
                <HazardTag hazardClass={permit.hazardClass} />
              </>
            }
            helperText={
              !editable
                ? t('permits.detail.readOnlyNote', {
                    status: t(getPermitStatusPresentation(permit.status).labelKey),
                  })
                : undefined
            }
            actions={
              <ButtonSet>
                {editable ? (
                  <Button variant="ghost" onClick={() => setEditOpen(true)}>
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                    {t('permits.detail.edit')}
                  </Button>
                ) : null}
                <PermitLifecycleActions
                  permit={permit}
                  canApprove={detail.canApprove}
                  canReview={detail.canReview}
                />
              </ButtonSet>
            }
          />

          <DetailLayout
            meta={
              <>
                <Card>
                  <CardContent className="flex flex-col gap-4">
                    <h2 className="text-sm font-semibold text-fg">
                      {t('permits.detail.section.details')}
                    </h2>
                    <dl className="grid grid-cols-2 gap-4">
                      <Fact
                        className="col-span-2"
                        label={t('permits.detail.field.contractor')}
                        value={permit.contractorName}
                      />
                      <SitesFact
                        className="col-span-2"
                        label={t('permits.detail.field.sites')}
                        sites={permit.sites}
                      />
                      <Fact
                        label={t('permits.form.field.validityStart')}
                        value={formatDateTime(permit.validityStart, culture)}
                      />
                      <Fact
                        label={t('permits.form.field.validityEnd')}
                        value={formatDateTime(permit.validityEnd, culture)}
                      />
                      <Fact
                        label={t('permits.detail.field.approvedBy')}
                        value={permit.approvedByName}
                      />
                      <Fact
                        label={t('permits.detail.field.approvedAt')}
                        value={
                          permit.approvedAt ? formatDateTime(permit.approvedAt, culture) : null
                        }
                      />
                      <Fact
                        label={t('permits.detail.field.createdAt')}
                        value={permit.createdAt ? formatDate(permit.createdAt, culture) : null}
                      />
                    </dl>
                    {permit.description ? (
                      <>
                        <hr className="border-border" />
                        <Fact
                          label={t('permits.detail.field.description')}
                          value={permit.description}
                        />
                      </>
                    ) : null}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="flex flex-col gap-4">
                    <h2 className="text-sm font-semibold text-fg">
                      {t('permits.detail.section.progress')}
                    </h2>
                    <ProgressIndicator
                      steps={buildPermitProgressSteps(
                        permit,
                        t,
                        permit.approvedAt ? formatDateTime(permit.approvedAt, culture) : null,
                      )}
                    />
                  </CardContent>
                </Card>
              </>
            }
            content={
              <div className="flex min-w-0 flex-col gap-4">
                {issueCount > 0 ? (
                  <Alert tone="warning">
                    <p className="font-semibold">
                      {t(
                        permitIssuesPastApproval(permit.status)
                          ? 'permits.detail.issues.entryMessage'
                          : 'permits.detail.issues.message',
                        { count: issueCount },
                      )}
                    </p>
                    {issueFactsLine.length > 0 ? (
                      <p className="mt-0.5 text-muted carbon:text-xs">{issueFactsLine}</p>
                    ) : null}
                  </Alert>
                ) : null}

                {/* Tabs + Tile ARASINDA boşluk YOK (README "Tile sits directly under the tab
                    list", coordinator bulgusu) — bu iç sarmalayıcı `gap-4`'süz, dış sütunun
                    `gap-4`'ü yalnız [uyarı]→bu blok arasında kalır. */}
                <div className="flex min-w-0 flex-col">
                  <Tabs
                    items={tabItems}
                    value={activeTab}
                    onValueChange={(value) => setSelectedTab(value as DetailTab)}
                    ariaLabel={t('permits.title')}
                    idPrefix="permit-detail"
                  />

                  {/* Her sekme artık kendi `TableToolbar`/toolbar-şeridi + `Table`'ını taşır —
                      Carbon Tile'da bu ikisi KENDİ kenarına kadar uzanır (README "toolbar flush,
                      table full-bleed"), bu yüzden dış `Card`'ın 16px dolgusu TÜM sekmelerde
                      sıfırlanır (S33 Slice 6 — Slice 3'ün yalnız Personel'e uyguladığı istisna
                      artık genel kural, Araçlar/Kontrol/Eğitim/KKD/Ek Belge/Aksiyonlar da aynı
                      anatomiye taşındı). */}
                  <Card className="carbon:p-0">
                    <CardContent className="carbon:p-0">
                      <TabPanel value={activeTab} activeValue={activeTab} idPrefix="permit-detail">
                        {activeTab === 'vehicles' &&
                        hasVehicleSection(permit.hasVehicles, detail.vehicles) ? (
                          <PermitVehiclesPanel
                            permitUuid={permit.uuid}
                            contractorUuid={permit.contractorUuid}
                            vehicles={detail.vehicles}
                            requiredVehicleDocuments={detail.requiredVehicleDocuments}
                            source={{
                              dataUpdatedAt: query.dataUpdatedAt,
                              errorUpdatedAt: query.errorUpdatedAt,
                            }}
                          />
                        ) : activeTab === 'activities' ? (
                          <PermitActivityPanel permitUuid={permit.uuid} />
                        ) : activeTab === 'trainings' ? (
                          <RequiredTrainingsPanel
                            permitUuid={permit.uuid}
                            requiredTrainings={detail.requiredTrainings}
                          />
                        ) : activeTab === 'ppe' ? (
                          <PermitProtectiveEquipmentPanel
                            permitUuid={permit.uuid}
                            protectiveEquipment={detail.requiredProtectiveEquipment}
                          />
                        ) : activeTab === 'controls' ? (
                          <PermitControlChecklistPanel
                            permitUuid={permit.uuid}
                            controlItems={detail.controlItems}
                            permitStatus={permit.status}
                          />
                        ) : activeTab === 'docRequests' ? (
                          <PermitDocumentRequestsPanel
                            permitUuid={permit.uuid}
                            documentRequests={detail.documentRequests}
                            canEdit={editable}
                          />
                        ) : (
                          <PermitPersonnelPanel
                            permitUuid={permit.uuid}
                            contractorUuid={permit.contractorUuid}
                            canCreatePersonnel={detail.canCreatePersonnel === true}
                            personnel={detail.personnel}
                            requiredDocuments={detail.requiredDocuments}
                            documentRequests={detail.documentRequests}
                            source={{
                              dataUpdatedAt: query.dataUpdatedAt,
                              errorUpdatedAt: query.errorUpdatedAt,
                            }}
                          />
                        )}
                      </TabPanel>
                    </CardContent>
                  </Card>
                </div>
              </div>
            }
          />

          {editable ? (
            <PermitFormModal
              open={editOpen}
              onOpenChange={setEditOpen}
              permit={permit}
              // Bilgi değişikliği SPEC §5/§6 uyarınca backend'de Aksiyonlar'a SystemLog (ChangeSet)
              // düşer; akışın yeniden çekilmesi için activities anahtarını da invalidate ederiz
              // (form zaten detail/list'i invalidate eder).
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: permitsKeys.activities(permit.uuid) });
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
